import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DetailConfigurationField} from "../../models/detail-configuration-field";
import {TypeaheadMatch} from "ngx-bootstrap/typeahead";

const noop = () => {
};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => TagsInputComponent),
	multi: true
};

@Component({
	selector: "tig-tags-input",
	templateUrl: "tags-input.component.html",
	styleUrls: ["tags-input.component.css"],
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class TagsInputComponent implements OnInit, ControlValueAccessor {
	private STR_selected: string = "";
	public ARR_tags: any[] = [];
	private onTouchedCallback: () => void = noop;
	private onChangeCallback: (_: any) => void = noop;
	public with_space = false;

	private minlength: string = "-1";
	private minlength_error: string = "minlength error";
	private maxlength: string = "-1";
	private maxlength_error: string = "maxlength error";
	private pattern: string = ".*";
	private pattern_error: string = "pattern error";
	private required: boolean = false;
	private required_error: string = "required error";

	@Input() INT_maxTags: number;
	@Input() BOO_removeLastOnBackspace: boolean = true;
	@Input() BOO_canDeleteTags: boolean = true;
	@Input() STR_placeholder: string = "";
	@Input() STR_secondaryPlaceholder: string = "";
	@Input() ARR_options: any = null;
	@Input() STR_displayField: string = "displayValue";
	@Input() INT_minLengthBeforeOptions: number = 1;
	@Input() BOO_scrollableOptions: boolean = false;
	@Input() INT_scrollableOptionsInView: number = 5;
	@Input() BOO_objectTags: boolean = false;
	@Input() BOO_readonly: boolean = false;
	@Input() OBJ_field_config: DetailConfigurationField;
	@Output() onTagsChanged = new EventEmitter();
	@Output() onInputValidation = new EventEmitter();
	@Output() onMaxTagsReached = new EventEmitter();
	@Output() onNoOptionsMatch = new EventEmitter();


	/**
	 * Function > ngOnInit
	 */
	ngOnInit() {

		if (this.OBJ_field_config.validators) {

			this.OBJ_field_config.validators.forEach((OBJ_validator) => {
				this[OBJ_validator.type] = OBJ_validator.value;
				this[OBJ_validator.type + '_error'] = OBJ_validator.error;
			});

		}

		if (
			typeof this.OBJ_field_config.config !== "undefined" &&
			typeof this.OBJ_field_config.config.with_space !== "undefined"
		) {
			this.with_space = this.OBJ_field_config.config.with_space;
		}

	}


	/**
	 * Function > getPlaceHolder
	 */
	private getPlaceHolder(): string {

		if (this.ARR_tags && this.ARR_tags.length > 0) {
			return this.STR_secondaryPlaceholder;
		} else if (this.BOO_readonly) {
			return "";
		} // end if

		return this.STR_placeholder;

	} // end function getPlaceHolder


	/**
	 * Function > tagsChanged
	 * @param {string} type
	 * @param {any} tag
	 */
	private tagsChanged(type: string, tag: any): void {

		this.onChangeCallback(this.ARR_tags);
		this.onInputValidation.emit({count: this.ARR_tags.length});
		this.onTagsChanged.emit({
			change: type,
			tag: tag,
			tags: this.ARR_tags
		});

		if (this.maximumOfTagsReached()) {
			this.onMaxTagsReached.emit();
		} // end if

	} // function tagsChanged


	/**
	 * Function > removeLastTag
	 * @param {HTMLInputElement} tagInput
	 */
	private removeLastTag(tagInput: HTMLInputElement): void {

		if (!this.BOO_removeLastOnBackspace || !this.ARR_tags.length) {
			return;
		} // end if

		if (tagInput.value === "") {
			this.removeTag(this.ARR_tags[this.ARR_tags.length - 1]);
		} // end if

	} // end function removeLastTag


	/**
	 * Function > findError
	 * @param OBJ_valid_inputs
	 */
	private findError(OBJ_valid_inputs) {

		for (let item in OBJ_valid_inputs) {

			if (OBJ_valid_inputs[item]) {

				switch (item) {

					case 'patternMismatch':
						return this.pattern_error;

					case 'tooLong':
						return this.maxlength_error;

					case 'tooShort':
						return this.minlength_error;

					case 'valueMissing':
						return this.required_error;

					case 'required':
						return this.required_error;

				} // end switch

			} // end if

		} // end for

	} // end function findError


	/**
	 * Function > addTag
	 * @param {HTMLInputElement} tagInput
	 */
	private addTag(tagInput: HTMLInputElement): void {

		if (!tagInput.validity.valid) {

			let errormessage = this.findError(tagInput.validity);
			this.onInputValidation.emit(errormessage);

		} else {

			if (tagInput.value.trim() !== "") {

				if (this.BOO_objectTags) {

					let tag = {
						[this.STR_displayField]: tagInput.value
					};
					this.addPredefinedTag(tag);

				} else {
					this.addPredefinedTag(tagInput.value);
				} // end if

			} // end if

			// this.onChangeCallback(this.ARR_tags);
			tagInput.value = "";

		} // end if

	} // end function addTag


	/**
	 * Function > addPredefinedTag
	 * @param {Object} tag
	 */
	private addPredefinedTag(tag: Object): void {

		if (!this.maximumOfTagsReached()) {
			this.ARR_tags.push(tag);
			this.tagsChanged("add", tag);
		} // end if

	} // end function addPredefinedTag


	/**
	 * Function > removeTag
	 * @param tagToRemove
	 */
	private removeTag(tagToRemove: any): void {

		if (!this.isDeletable(tagToRemove)) {
			return;
		} // end if

		this.ARR_tags = this.ARR_tags.filter(tag => tagToRemove !== tag);
		this.tagsChanged("remove", tagToRemove);

	} // end function removeTag


	private maximumOfTagsReached(): boolean {
		return typeof this.INT_maxTags !== "undefined" && this.ARR_tags.length >= this.INT_maxTags;
	}


	private disableField(): boolean {
		return this.BOO_readonly || this.maximumOfTagsReached();
	}


	private isDeletable(tag: any) {
		if (typeof tag.deleteable !== "undefined" && !tag.deleteable) {
			return false;
		} else if (this.BOO_readonly) {
			return false;
		} // end if
		return this.BOO_canDeleteTags;
	}


	private typeaheadOnSelect(e: TypeaheadMatch): void {
		if (typeof e.item === "string") {
			this.addPredefinedTag({
				[this.STR_displayField]: e.value
			});
		} else {
			this.addPredefinedTag(e.item);
		}
		this.STR_selected = "";
	}


	private typeaheadOnNoMatch(e: any): void {
		if (typeof this.onNoOptionsMatch !== "undefined") {
			this.onNoOptionsMatch.emit(e);
		}
	}


	/**
	 * Function > writeValue
	 * @param {any} value
	 */
	writeValue(value: any) {

		if (value !== this.ARR_tags) {
			this.ARR_tags = value;
		} // end if

	} // end function writeValue


	registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}


	registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}
}
